import { Link, router } from '@inertiajs/react'
import { type RefObject, useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useI18n } from '../lib/useI18n'
import Icon from './components/Icon'
import { bucket, look, TONE_STYLE, timeAgo } from './notificationStyle'

// Top-bar notification dropdown (mockups `.noti`) — opens from the bell, fetches
// the most recent deliveries on open, and offers All / Unread tabs, mark-all-
// read, and a "View all" footer that opens the full notification center. Shared
// by the operator + portal shells; the surface passes `href` for "View all".

type Item = {
  id: string
  event: string
  title: string
  body: string | null
  reference: string | null
  reference_kind: string | null
  url: string | null
  read: boolean
  created_at: string
}

export default function NotificationDropdown({
  anchorRef,
  open,
  onClose,
  href,
}: {
  anchorRef: RefObject<HTMLElement | null>
  open: boolean
  onClose: () => void
  href: string
}) {
  const { t } = useI18n()
  const [items, setItems] = useState<Item[]>([])
  const [unread, setUnread] = useState(0)
  const [tab, setTab] = useState<'all' | 'unread'>('all')
  const [loading, setLoading] = useState(false)
  const [pos, setPos] = useState<{ top: number; right: number }>({ top: 56, right: 14 })

  const load = useCallback(() => {
    setLoading(true)
    fetch('/notifications/recent', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : { items: [], unread_count: 0 }))
      .then((data: { items: Item[]; unread_count: number }) => {
        setItems(data.items ?? [])
        setUnread(data.unread_count ?? 0)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  // Position under the bell, right-aligned (mockup `.noti{ top:56px; right:14px }`).
  useLayoutEffect(() => {
    if (!open) return
    const rect = anchorRef.current?.getBoundingClientRect()
    if (rect) setPos({ top: rect.bottom + 8, right: Math.max(8, window.innerWidth - rect.right) })
  }, [open, anchorRef])

  useEffect(() => {
    if (open) {
      setTab('all')
      load()
    }
  }, [open, load])

  // Close on Escape.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const shown = tab === 'unread' ? items.filter((i) => !i.read) : items

  function openItem(item: Item) {
    if (!item.read) router.patch(`/notifications/${item.id}`, {}, { preserveScroll: true })
    onClose()
    // A ready-export links to a file download — use a native navigation so the
    // browser downloads it, rather than an Inertia XHR visit.
    if (item.event === 'document.exported' && item.url) {
      window.location.href = item.url
    } else {
      router.visit(item.url ?? href)
    }
  }

  function markAll() {
    router.delete('/notifications', {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => {
        setItems((prev) => prev.map((i) => ({ ...i, read: true })))
        setUnread(0)
      },
    })
  }

  let lastBucket = ''

  return createPortal(
    // biome-ignore lint/a11y/noStaticElementInteractions: full-screen click-catcher to dismiss the panel
    <div className="fixed inset-0 z-[85]" onMouseDown={onClose}>
      <div
        role="dialog"
        aria-label="Notifications"
        className="absolute flex max-h-[80vh] w-[394px] max-w-[calc(100vw-20px)] flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-[var(--shadow)]"
        style={{ top: pos.top, right: pos.right }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* head */}
        <div className="flex items-center gap-2.5 border-border border-b px-4 pt-3.5 pb-3">
          <h3 className="font-display text-[15px] font-bold">{t('notif.title')}</h3>
          <button
            type="button"
            onClick={markAll}
            disabled={unread === 0}
            className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-accent hover:underline disabled:opacity-40 disabled:no-underline"
          >
            <Icon name="check-check" size={14} /> {t('notif.mark_all')}
          </button>
        </div>

        {/* tabs */}
        <div className="flex gap-1 border-border border-b px-3 pt-2">
          {(['all', 'unread'] as const).map((tabKey) => (
            <button
              key={tabKey}
              type="button"
              onClick={() => setTab(tabKey)}
              className={`rounded-t-lg px-3 py-2 text-[12.5px] font-semibold ${
                tab === tabKey
                  ? 'border-accent border-b-2 text-accent'
                  : 'border-transparent border-b-2 text-ink-2 hover:text-ink'
              }`}
            >
              {t(`notif.tab.${tabKey}`)}
              {tabKey === 'unread' && unread > 0 ? ` (${unread})` : ''}
            </button>
          ))}
        </div>

        {/* list */}
        <div className="flex-1 overflow-y-auto">
          {loading && shown.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-ink-muted">{t('notif.loading')}</p>
          ) : shown.length === 0 ? (
            <div className="px-5 py-12 text-center text-ink-muted">
              <Icon name="bell-off" size={28} className="mx-auto" />
              <div className="mt-2.5 font-semibold">{t('notif.empty.title')}</div>
              <div className="mt-1 text-xs">
                {t(tab === 'unread' ? 'notif.empty.unread' : 'notif.empty.all')}
              </div>
            </div>
          ) : (
            shown.map((item) => {
              const { icon, tone } = look(item.event)
              const sec = bucket(item.created_at, t)
              const showSec = sec !== lastBucket
              lastBucket = sec
              return (
                <div key={item.id}>
                  {showSec ? (
                    <div className="sticky top-0 bg-surface-2 px-4 pt-2.5 pb-1 font-bold text-[10px] text-ink-muted uppercase tracking-[0.08em]">
                      {sec}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => openItem(item)}
                    className={`relative flex w-full gap-3 border-border border-b px-4 py-3 text-left hover:bg-surface-2 ${
                      item.read ? '' : 'bg-[var(--accent-soft)]'
                    }`}
                  >
                    <span
                      className="grid h-[34px] w-[34px] flex-none place-items-center rounded-[10px]"
                      style={{ backgroundColor: TONE_STYLE[tone].bg, color: TONE_STYLE[tone].fg }}
                    >
                      <Icon name={icon} size={17} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] font-semibold leading-tight">
                        {item.title}
                      </span>
                      {item.reference && item.reference_kind ? (
                        <span className="mt-0.5 block text-xs font-medium text-ink-2 leading-snug">
                          {t(`notif.ref.${item.reference_kind}`, { ref: item.reference })}
                        </span>
                      ) : null}
                      {item.body ? (
                        <span className="mt-0.5 block text-xs text-ink-2 leading-snug">
                          {item.body}
                        </span>
                      ) : null}
                      <span className="mt-1 block text-[11px] text-ink-muted">
                        {timeAgo(item.created_at, t)}
                      </span>
                    </span>
                    {item.read ? null : (
                      <span className="mt-1.5 h-2 w-2 flex-none rounded-full bg-accent" />
                    )}
                  </button>
                </div>
              )
            })
          )}
        </div>

        {/* foot */}
        <div className="border-border border-t p-2.5">
          <Link
            href={href}
            onClick={onClose}
            className="flex items-center justify-center gap-2 rounded-[9px] px-4 py-2 text-[13px] font-semibold text-ink-2 hover:bg-surface-3 hover:text-ink"
          >
            {t('notif.view_all')}
          </Link>
        </div>
      </div>
    </div>,
    document.body,
  )
}
